import { NodeReadingData } from '@/contexts/RealtimeDataContext';
import dayjs, { Dayjs } from 'dayjs';
import { transformMultipleNodes } from '@/utils/nodeDataTransformer';
import { PlayerChartProvider } from '@/components/General/PlayerChart/context';
import { PlayerChartConfig } from '@/components/General/PlayerChart/types';
import { PlayerChart, TimelinePlayer } from '@/components/General/PlayerChart/components';
import { useEffect, useMemo, useCallback } from 'react';
import { usePlayerChart } from '@/components/General/PlayerChart/context/usePlayerChart';
import { Switch, Drawer } from 'antd';
import DrawerHeaderWithControls from '@/components/DiagramBlock/DrawerChartPlayer/DrawerHeaderWithControls';

function ChartPlayerListener({
	onStateChange,
}: {
	onStateChange?: DrawerChartPlayerProps['onPlayerStateChange'];
}) {
	const { isPlaying, currentIndex, totalTimestamps, currentTimestamp, minTimestamp, maxTimestamp } =
		usePlayerChart();

	const stateData = useMemo(() => {
		const currentTime = currentTimestamp ? dayjs(currentTimestamp).toISOString() : null;
		const isAtEnd = currentIndex >= totalTimestamps - 1;

		let progress = 0;
		if (currentTimestamp && minTimestamp && maxTimestamp) {
			const totalDuration = maxTimestamp - minTimestamp;
			const currentDuration = currentTimestamp - minTimestamp;
			progress = totalDuration > 0 ? (currentDuration / totalDuration) * 100 : 0;
		}

		return {
			isPlaying,
			currentTime,
			progress,
			isAtEnd,
		};
	}, [isPlaying, currentIndex, totalTimestamps, currentTimestamp, minTimestamp, maxTimestamp]);

	const handleStateChange = useCallback((state: typeof stateData) => {
		if (onStateChange) {
			onStateChange(state);
		}
	}, [onStateChange]);

	useEffect(() => {
		handleStateChange(stateData);
	}, [stateData, handleStateChange]);

	return null;
}

type DateInput = string | number | Date | Dayjs;

interface DrawerChartPlayerProps {
	chartStartDate: DateInput;
	chartEndDate: DateInput;
	chartNodeData: NodeReadingData[];
	isChartDataLoading: boolean;
	isDrawerVisible: boolean;
	drawerTitle: string;
	onDrawerVisibilityChange: (isVisible: boolean) => void;
	onPlayerStateChange?: (state: {
		isPlaying: boolean;
		currentTime: string | null;
		progress: number;
		isAtEnd: boolean;
	}) => void;
}

function DrawerChartPlayer(props: DrawerChartPlayerProps) {
	const instanceId = 'drawer-chart-player';
	
	const { 
		chartStartDate, 
		chartEndDate, 
		chartNodeData, 
		onDrawerVisibilityChange,
		isDrawerVisible,
		drawerTitle,
		isChartDataLoading,
		onPlayerStateChange
	} = props;
	
	const { startDate, endDate } = useMemo(() => {
		const start = dayjs(chartStartDate).isValid()
			? dayjs(chartStartDate).toDate()
			: null;
		const end = dayjs(chartEndDate).isValid() 
			? dayjs(chartEndDate).toDate() 
			: null;
		
		return { startDate: start, endDate: end };
	}, [chartStartDate, chartEndDate]);

	const nodeChartData = useMemo(() => {
		return transformMultipleNodes(chartNodeData);
	}, [chartNodeData]);

	const playerChartConfig: PlayerChartConfig = useMemo(() => ({
		chartData: nodeChartData,
		playerSpeed: 250,
		dateRange: startDate && endDate ? {
			startDate: startDate,
			endDate: endDate,
		} : undefined,
	}), [nodeChartData, startDate, endDate]);

	const handleSwitchChange = useCallback((checked: boolean) => {
		onDrawerVisibilityChange(checked);
	}, [onDrawerVisibilityChange]);

	const handleDrawerClose = useCallback(() => {
		onDrawerVisibilityChange(false);
	}, [onDrawerVisibilityChange]);

	if (!startDate || !endDate) {
		return null;
	}

	return (
		<PlayerChartProvider config={playerChartConfig} isVisible={isDrawerVisible} instanceId={instanceId}>
			<ChartPlayerListener onStateChange={onPlayerStateChange} />

			<div className="fixed bottom-5 right-5 z-[1000] bg-white rounded-md p-2 shadow-lg flex items-center justify-center">
				<Switch
					checked={isDrawerVisible}
					onChange={handleSwitchChange}
					checkedChildren="Show Trends"
					unCheckedChildren="Show Trends"
					size="default"
					disabled={isChartDataLoading}
				/>
			</div>

			<Drawer
				title={<DrawerHeaderWithControls title={drawerTitle} onClose={handleDrawerClose} />}
				placement="bottom"
				onClose={handleDrawerClose}
				open={isDrawerVisible}
				height="330px"
				styles={{ 
					body: { padding: 0, overflow: 'visible' },
					wrapper: { overflow: 'visible' }
				}}
				maskClosable={false}
				closable={false}
				mask={false}
				zIndex={1000}
			>
				{isChartDataLoading ? (
					<div className="w-full h-full flex items-center justify-center">
						<div className="flex flex-col items-center gap-4">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
							<span className="text-gray-600">Loading readings...</span>
						</div>
					</div>
				) : (
					<div className="w-full pt-5" style={{ overflow: 'visible' }}>
						<TimelinePlayer />
						<PlayerChart highChartHeight={200} tooltipPositionTop={true} />
					</div>
				)}
			</Drawer>
		</PlayerChartProvider>
	);
}

export default DrawerChartPlayer;
